<script lang="ts">
  import ApprovedProducts from "@retailer/svelte/ProductLines/OrderActionBar/Cards/ApprovedProducts.svelte";
  import EasilyOrderAgain from "@retailer/svelte/ProductLines/OrderActionBar/Cards/EasilyOrderAgain.svelte";
  import DistributorCatalog from "@retailer/svelte/ProductLines/OrderActionBar/Cards/DistributorCatalog.svelte";
  import WhatsNew from "@retailer/svelte/ProductLines/OrderActionBar/Cards/WhatsNew.svelte";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import QuickAction from "@retailer/svelte/ProductLines/OrderActionBar/QuickAction.svelte";
  import { isAutoAssignedToRetailer } from "@retailer/services/distributor_service";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import DistributorsStore from "@shared/svelte/Stores/DistributorsStore";
  import Authorization from "@shared/svelte/Components/Authorization.svelte";
  import { pluralize } from "@shared/svelte/Modules/pluralize";

  import { getControlStateAuthenticationService } from "@retailer/services/retailer_user_authentications_service";

  import type { Distributor } from "@shared/types/Distributor";
  import type { RetailerType } from "@shared/types/Retailer";
  import type { RetailerUser } from "@shared/types/User";
  import RetailerUserAuthenticationsStore from "@retailer/svelte/stores/RetailerUserAuthenticationsStore";

  export let user: RetailerUser;
  export let retailer: RetailerType;

  const hasOrdered = !!retailer?.last_ordered_at;

  let distributors: Distributor[] = [];
  let innerWidth = window.innerWidth;
  let listCount = -1;

  const fetchListCount = () => {
    HttpPromise({
      method: "GET",
      url: "/api/retailer/quick_order_lists",
      data: {
        count: true,
      },
    }).then(result => {
      if (result?.count) {
        listCount = result.count;
      }
    });
  };

  const wasDistributorManuallyAdded = (distributor: Distributor) => {
    const hasSalesRepsOrDirectAdd =
      (distributor.sales_reps && distributor.sales_reps.length > 0) ||
      distributor.retailer_direct_add;
    return distributor && !isAutoAssignedToRetailer(distributor) && hasSalesRepsOrDirectAdd;
  };

  $: distributors = $DistributorsStore?.filter(wasDistributorManuallyAdded);
  $: distributorCount = distributors?.length;

  fetchListCount();

  let authService = getControlStateAuthenticationService();
  let authStatus: string;
  $: authStatus = authService?.getRetailerUserAuthenticationStatus?.(
    $RetailerUserAuthenticationsStore,
  );
</script>

<svelte:window bind:innerWidth />
<div class="fizz-styles">
  {#if hasOrdered}
    <div id="action-bar-2-has-orders" class="fizz-stack-32">
      <div class="fizz-surface-blue mb-32">
        <div class="max-w-cap mx-auto flex flex-wrap gap-24 p-32 justify-between items-center">
          <div class="fizz-max-content">
            <h1 class="fizz-heading-2">Welcome to Provi, {user.first_name}!</h1>
            <p>Here's what's on tap for {retailer.name} today</p>
          </div>
          <div class="flex flex-wrap gap-16">
            <div class="flex flex-wrap gap-16">
              <QuickAction
                name="Order history"
                iconName="LiquorGlassBottleSolid"
                linkText={`${pluralize("order", retailer.orders_placed_count, true)} sent`}
                linkHref="layout.order_groups"
                targetObjectType="action_bar_widget_order_history_link"
              />
              <QuickAction
                name="Lists"
                iconName="ListSolid"
                linkText={listCount < 0 ? "" : pluralize("list", listCount, true)}
                linkHref="layout.quick_order_list_index"
                targetObjectType="action_bar_widget_lists_link"
              />
            </div>
            <div class="flex flex-wrap gap-16">
              {#if retailer.term_payments}
                <Authorization authName="invoices.index">
                  <QuickAction
                    name="ProviPay"
                    iconName="DollarSignCircleSolid"
                    linkText="{retailer.unpaid_invoices_count} unpaid {pluralize(
                      'invoice',
                      retailer.unpaid_invoices_count,
                      false,
                    )}"
                    linkHref="layout.invoices"
                    targetObjectType="action_bar_widget_provi_pay_link"
                  />
                </Authorization>
              {/if}
              <QuickAction
                name="My distributors"
                iconName="DeliveryTruckSolid"
                linkText={distributorCount ? pluralize("distributor", distributorCount, true) : ""}
                linkHref="layout.accounts"
                targetObjectType="action_bar_widget_my_distributors_link"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="fizz-container fizz-flex-row-8 fizz-inset-horizontal-32 overflow-x-auto">
        {#if authService && authStatus == "pending" && authService?.actionBarCtaComponent}
          <svelte:component this={authService?.actionBarCtaComponent} {authService} />
        {:else if retailer.limited_ordering && stagedRolloutCheck("adm__dynamic_limited_ordering_filters")}
          <ApprovedProducts />
        {:else}
          <WhatsNew />
        {/if}
        <EasilyOrderAgain />
        <DistributorCatalog {distributors} />
      </div>
    </div>
  {/if}
</div>
